import Breadcrumbs from "@/components/design-system/breadcrumbs";
import { Button } from "@/components/design-system/button";
import { Input } from "@/components/design-system/input";
import SelectWithCheckbox from "@/components/design-system/SelectWithCheckbox";
import SelectWithRadio from "@/components/design-system/SelectWithRadio";
import {
  getAssignResourceDropdownV2Service,
  getLessonsDropdownService,
  getUnitsDropdownService,
  uploadFileService,
  type AssignResourceType,
} from "@/service/base.service";
import { getAllStudentV2Service } from "@/service/student.service";
import { AppDispatch, RootState } from "@/store";
import {
  createAssignment,
  selectAssignmentActionLoading,
  setActionLoading,
} from "@/store/slices/assignmentSlice";

import { AssignmentFormData } from "@/types/assignment.model";
import type { StudentListType } from "@/types/student.model";
import { getLessonDropdownQuery } from "@/utils/assignmentRecordParams";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import FileUploadSection from "../components/FileUploadSection";

const AddAssignment: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();

  const user = useSelector((state: RootState) => state.auth.user);

  const [studentOptions, setStudentOptions] = useState<
    { value: string; label: string }[]
  >([]);

  const [resourceOptions, setResourceOptions] = useState<
    { id: string; label: string }[]
  >([]);
  const [resourceLoading, setResourceLoading] = useState(false);
  const [lessons, setLessons] = useState<{ label: string; value: string }[]>(
    []
  );
  const [units, setUnits] = useState<{ label: string; value: string }[]>([]);

  const actionLoading = useSelector(selectAssignmentActionLoading);

  // Enhanced handleFileUpload function to handle both regular Files and our enhanced blob objects
  const handleFileUpload = (file: File | null) => {
    if (!file) {
      setValue("attachment", undefined, { shouldValidate: true });
      return;
    }

    // setUploadedFiles(file);
    setValue("attachment", file, { shouldValidate: true });
  };

  const {
    handleSubmit,
    formState: { errors, isValid },
    watch,
    setValue,
    register,
    trigger,
  } = useForm<AssignmentFormData>({
    defaultValues: {
      name: "",
      type: "course",
      grade: "",
      cs_type: "general",
      lesson: "",
      unit: "",
      notes: "",
      students: [],
    },
    mode: "onBlur",
    reValidateMode: "onChange",
    criteriaMode: "firstError",
  });

  const resourceType = (watch("type") as AssignResourceType) || "course";

  const fetchResourceOptions = async (type: AssignResourceType) => {
    setResourceLoading(true);
    try {
      const { data } = await getAssignResourceDropdownV2Service(type);
      const items = (data.data ?? []).map((item) => {
        if (type === "course" && "grade" in item) {
          return { id: item.id, label: item.grade };
        }
        if ("name" in item) {
          return { id: item.id, label: item.name };
        }
        return { id: item.id, label: item.id };
      });
      setResourceOptions(items);
    } catch (error) {
      console.error("Error fetching assign resources:", error);
      setResourceOptions([]);
    } finally {
      setResourceLoading(false);
    }
  };

  useEffect(() => {
    void fetchResourceOptions(resourceType);
  }, [resourceType]);

  const resourceLabel =
    resourceType === "class" ? "Select Class" : "Select Grade";
  const resourcePlaceholder = resourceLoading
    ? "Loading…"
    : resourceType === "class"
      ? "Select class"
      : "Select grade";
  const resourceRequiredMessage =
    resourceType === "class" ? "Class is required" : "Grade is required";

  const fetchStudents = async (resourceId?: string) => {
    if (!resourceId) return;

    const listType: StudentListType = resourceType;

    try {
      const { data } = await getAllStudentV2Service({
        page: 1,
        pageSize: 500,
        type: listType,
        teacherId: user?.teacher_id,
        schoolId: user?.school_id,
        ...(listType === "class"
          ? { classId: resourceId }
          : { gradeId: resourceId }),
      });

      if (data?.data) {
        setStudentOptions(
          data.data.map((student) => ({
            value: student.user_id ?? student.id,
            label: student.name,
          }))
        );
      } else {
        setStudentOptions([]);
      }
    } catch (error) {
      console.error("Failed to fetch students:", error);
      setStudentOptions([]);
    }
  };

  const fetchLessons = async (resourceId?: string) => {
    if (!resourceId) return;

    try {
      const { data } = await getLessonsDropdownService(
        getLessonDropdownQuery(resourceType, resourceId)
      );

      if (data && data.data) {
        const lessonOptions = data.data.map(
          (lesson: { id: string; lesson: string }) => ({
            value: lesson.id,
            label: `${lesson.lesson}`,
          })
        );
        setLessons(lessonOptions);
      }
    } catch (error) {
      console.error("Failed to fetch lessons:", error);
    }
  };

  const fetchUnits = async (lessonId?: string) => {
    try {
      const { data } = await getUnitsDropdownService({
        search: "",
        lessonId,
      });

      if (data && data.data) {
        const unitOptions = data.data.map(
          (unit: { id: string; unit: string }) => ({
            value: unit.id,
            label: `${unit.unit}`,
          })
        );
        setUnits(unitOptions);
      }
    } catch (error) {
      console.error("Failed to fetch units:", error);
    }
  };

  const buildCreatePayload = (
    data: AssignmentFormData,
    uploads?: string[]
  ): AssignmentFormData => {
    const payload: AssignmentFormData = {
      name: data.name,
      type: data.type ?? "course",
      grade: data.grade,
      cs_type: data.cs_type === "general" ? "GENERAL" : "UNIT",
      students: data.students,
      notes: data.notes,
    };

    if (uploads?.length) {
      payload.uploads = uploads;
    }

    if (data.cs_type === "unit") {
      payload.lesson = data.lesson as string;
      payload.unit = data.unit as string;
    }

    return payload;
  };

  const onFormSubmit = async (data: AssignmentFormData) => {
    try {
      dispatch(setActionLoading(true));

      if (data.attachment) {
        try {
          const {
            data: {
              data: { id },
            },
          } = await uploadFileService(data.attachment);

          await dispatch(
            createAssignment({
              assignmentData: buildCreatePayload(data, [id]),
              navigate,
            })
          );
        } catch (error) {
          console.error("Failed to upload file:", error);
          dispatch(setActionLoading(false));
        }
        return;
      }

      await dispatch(
        createAssignment({
          assignmentData: buildCreatePayload(data),
          navigate,
        })
      );
    } catch (error: any) {
      console.error("Error submitting form:", error.message);
      dispatch(setActionLoading(false));
    }
  };

  const handleCancel = () => {
    navigate("/assignments");
  };

  return (
    <div className="h-full flex flex-col gap-6">
      <div className="bg-white rounded-[12px] h-full">
        <form
          onSubmit={handleSubmit(onFormSubmit)}
          className="w-full h-full flex flex-col"
        >
          <div className={`px-[32px] py-[33px] flex flex-col flex-1 mb-8`}>
            <div className="">
              <Breadcrumbs
                title={"Add Assignment"}
                onClick={() => navigate("/assignments")}
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <SelectWithRadio
                id="cs_type"
                label="Assignment Type"
                placeholder="Select assignment type"
                options={[
                  { value: "general", label: "General Assignment" },
                  { value: "unit", label: "Unit Assignment" },
                ]}
                value={watch("cs_type") || ""}
                onChange={(value) => {
                  const newType = value as "general" | "unit";
                  setValue("cs_type", newType, {
                    shouldValidate: true,
                  });

                  setValue("grade", "");

                  if (newType === "general") {
                    setValue("lesson", "");
                    setValue("unit", "");
                  }
                }}
                error={errors.cs_type?.message as string}
                required={true}
                showAbove={false}
                maxHeight="300px"
              />
            </div>

            <div className="w-full h-px bg-[#E2E8F0] mt-[48px] mb-[32px]"></div>

            <div className="mb-8">
              <h2 className="text-[#1F1F1F] text-[20px] font-semibold mb-6">
                General Details
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div key={"name"} className="w-full">
                  <Input
                    id={"name"}
                    {...register("name", {
                      required: "Assignment name is required",
                    })}
                    label="Assignment Name"
                    type="text"
                    placeholder="Enter name"
                    required={true}
                    maxLength={60}
                    errorMessage={errors.name?.message as string}
                  />
                </div>
                <div key={"resourceType"} className="w-full">
                  <SelectWithRadio
                    id="resourceType"
                    label="Type"
                    placeholder="Select type"
                    options={[
                      { value: "course", label: "Course" },
                      { value: "class", label: "Class" },
                    ]}
                    value={resourceType}
                    onChange={(value) => {
                      const nextType = value as AssignResourceType;
                      setValue("type", nextType, { shouldValidate: true });
                      setValue("grade", "");
                      setValue("students", []);
                      setValue("lesson", "");
                      setValue("unit", "");
                      setStudentOptions([]);
                      setLessons([]);
                      setUnits([]);
                    }}
                    required={true}
                    showAbove={false}
                    maxHeight="300px"
                  />
                  <input
                    type="hidden"
                    {...register("type", { required: "Type is required" })}
                  />
                </div>
                <div key={"grade"} className="w-full">
                  <SelectWithRadio
                    id="grade"
                    label={resourceLabel}
                    placeholder={resourcePlaceholder}
                    options={resourceOptions.map((item) => ({
                      value: item.id,
                      label: item.label,
                    }))}
                    disabled={resourceLoading}
                    value={(watch("grade") as string) || ""}
                    onChange={(value) => {
                      setValue("grade", value, {
                        shouldValidate: true,
                      });
                      fetchStudents(value);
                      setValue("students", []);
                      trigger("grade");

                      if (watch("cs_type") === "unit") {
                        fetchLessons(value);
                        setValue("lesson", "");
                        setValue("unit", "");
                      }
                    }}
                    required={true}
                    error={errors.grade?.message as string}
                    showAbove={false}
                    maxHeight="300px"
                  />
                  <input
                    type="hidden"
                    {...register("grade", { required: resourceRequiredMessage })}
                  />
                </div>
                {watch("cs_type") === "unit" && (
                  <>
                    <div key={"lessonId"} className="w-full">
                      <SelectWithRadio
                        id="lessonId"
                        label="Lesson Name"
                        placeholder="Select lesson"
                        options={lessons}
                        disabled={!watch("grade")}
                        value={(watch("lesson") as string) || ""}
                        onChange={(value) => {
                          setValue("lesson", value, { shouldValidate: true });
                          trigger("lesson");

                          fetchUnits(value);

                          setValue("unit", "");
                          // trigger("unitId");
                        }}
                        error={errors.lesson?.message as string}
                        showAbove={false}
                        maxHeight="300px"
                      />
                      <input
                        type="hidden"
                        {...register("lesson", {
                          required: "Lesson is required",
                        })}
                      />
                    </div>
                    <div key={"unitId"} className="w-full">
                      <SelectWithRadio
                        id="unitId"
                        label="Unit Name"
                        placeholder="Select unit"
                        options={units}
                        disabled={!watch("lesson")}
                        value={(watch("unit") as string) || ""}
                        onChange={(value) => {
                          setValue("unit", value, { shouldValidate: true });
                          trigger("unit");
                        }}
                        error={errors.unit?.message as string}
                        showAbove={true}
                        maxHeight="300px"
                      />
                      <input
                        type="hidden"
                        {...register("unit", { required: "Unit is required" })}
                      />
                    </div>
                  </>
                )}
                <div key={"studentIds"} className="w-full">
                  <SelectWithCheckbox
                    id="studentIds"
                    label="Students"
                    placeholder="Select student"
                    options={studentOptions}
                    value={(watch("students") as string[]) || []}
                    onChange={(value) =>
                      setValue("students", value, {
                        shouldValidate: true,
                      })
                    }
                    error={errors.students?.message as string}
                    showAbove={false}
                    maxHeight="300px"
                  />
                </div>
              </div>
            </div>

            <div className="w-full h-px bg-[#E2E8F0] mt-[48px] mb-[32px]"></div>

            <FileUploadSection
              onFileUpload={handleFileUpload}
              required={false}
              disabled={actionLoading}
            />

            <div className="mb-4">
              <p className="text-base font-medium text-[#2D3748] mb-2">
                Write a note
              </p>
              <textarea
                className="w-full border border-gray-200 rounded-2xl p-3 min-h-[100px] text-[14px] text-[#1F1F1F] font-[500] placeholder:text-[#A0AEC0] focus:outline-none"
                placeholder="Enter a note"
                value={watch("notes")}
                onChange={(e) => setValue("notes", e.target.value)}
                disabled={actionLoading}
              ></textarea>
            </div>
          </div>
          {/* Form Actions - Only show in add or edit mode */}
          <div className="flex h-[98px] px-[32px] justify-end space-x-4 border-t border-gray-200 pt-6 shadow-[0px_4px_24px_8px_#0000001F]">
            <>
              <Button
                type="button"
                className="h-[48px] w-[161px]"
                variant="primary-outline"
                onClick={handleCancel}
                disabled={actionLoading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="h-[48px] w-[161px] rounded-[8px] bg-[#5C88C5]"
                variant="primary"
                disabled={!isValid || actionLoading}
                loading={actionLoading}
              >
                Create
              </Button>
            </>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddAssignment;
